import { Card, CardContent } from "@/components/ui/card";
import type { ActivityAnalytics, AdminUserSummary } from "@/lib/api";
import { formatActionLabel } from "@/lib/format";

import { ActivityChart } from "./activity-chart";
import { ActivityExplorer } from "./activity-explorer";

interface ActivityDashboardProps {
  analytics: ActivityAnalytics;
  users: AdminUserSummary[];
  actions: string[];
}

export function ActivityDashboard({ analytics, users, actions }: ActivityDashboardProps) {
  const { summary, timeseries, breakdown } = analytics;

  const stats = [
    { label: "Events", value: summary.total_events },
    { label: "Logins", value: summary.total_logins },
    { label: "Active users", value: summary.active_users },
    { label: "Signups", value: summary.signups },
  ];

  const maxCount = Math.max(1, ...breakdown.map((b) => b.count));

  return (
    <div className="space-y-8">
      {/* Summary cards */}
      <section>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((stat) => (
            <Card key={stat.label}>
              <CardContent className="py-4">
                <p className="text-muted text-xs">{stat.label}</p>
                <p className="text-primary text-2xl font-bold tabular-nums">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-muted mt-2 text-xs">Last {summary.days} days</p>
      </section>

      {/* Timeseries chart */}
      <section>
        <h3 className="text-primary mb-3 text-sm font-bold">Activity over time</h3>
        <Card>
          <CardContent className="py-6">
            <ActivityChart data={timeseries} />
            <div className="text-muted mt-3 flex items-center justify-between text-xs">
              <span>{timeseries[0]?.day}</span>
              <span className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="bg-accent inline-block size-2 rounded-full" /> logins
                </span>
                <span className="flex items-center gap-1">
                  <span className="bg-accent/20 inline-block size-2 rounded-full" /> all events
                </span>
              </span>
              <span>{timeseries[timeseries.length - 1]?.day}</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Action breakdown */}
      {breakdown.length > 0 && (
        <section>
          <h3 className="text-primary mb-3 text-sm font-bold">By action</h3>
          <Card>
            <CardContent className="space-y-3 py-4">
              {breakdown.map((item) => (
                <div key={item.action} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-foreground font-medium">
                      {formatActionLabel(item.action)}
                    </span>
                    <span className="text-muted tabular-nums">{item.count}</span>
                  </div>
                  <div className="bg-secondary h-1.5 w-full overflow-hidden rounded-full">
                    <div
                      className="bg-accent h-full rounded-full"
                      style={{ width: `${(item.count / maxCount) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </section>
      )}

      {/* Filterable / sortable / paginated feed */}
      <section>
        <h3 className="text-primary mb-3 text-sm font-bold">Recent activity</h3>
        <ActivityExplorer users={users} actions={actions} />
      </section>
    </div>
  );
}
